import type { Agent } from "@/types/agent";
import { ArrowRight, Bot, Calculator, FileText, Settings, Truck, TrendingUp, DollarSign, MessageCircle, Users, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { categoryLabels } from "@/types/agent";

interface AgentCardProps {
  agent: Agent;
  onSettings?: (agent: Agent) => void;
  onEnter?: (agent: Agent) => void;
  className?: string;
}

const iconMap: Record<string, LucideIcon> = {
  Bot,
  Calculator,
  FileText,
  Truck,
  TrendingUp,
  DollarSign,
  MessageCircle,
};

function getIconComponent(iconName: string): LucideIcon {
  return iconMap[iconName] || Bot;
}

export const AgentCard: React.FC<AgentCardProps> = ({
  agent,
  onSettings,
  onEnter,
  className,
}) => {
  const IconComponent = getIconComponent(agent.icon);

  const handleEnterClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEnter?.(agent);
  };

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSettings?.(agent);
  };

  return (
    <div
      className={cn(
        "group rounded-xl border bg-card text-card-foreground shadow-sm",
        "transition-all duration-200",
        "hover:shadow-md hover:border-blue-200",
        "flex flex-col",
        className
      )}
    >
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start gap-4">
          <div
            className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: agent.iconBgColor }}
          >
            <IconComponent
              className="w-7 h-7"
              style={{ color: agent.iconColor }}
            />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold leading-tight tracking-tight text-foreground truncate">
              {agent.name}
            </h3>
            <span className="inline-flex mt-1.5 px-2 py-0.5 text-xs font-medium rounded-md bg-gray-100 text-gray-600">
              {categoryLabels[agent.category]}
            </span>
          </div>
        </div>

        <p className="mt-3 text-sm text-muted-foreground line-clamp-2 leading-relaxed">
          {agent.description}
        </p>

        <div className="mt-3 flex items-center gap-1 text-xs text-gray-400">
          <Users className="w-3.5 h-3.5" />
          <span>{agent.userCount.toLocaleString()} 人使用</span>
        </div>
      </div>

      <div className="px-5 pb-5 pt-0 flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleSettingsClick}
          className="flex-1 h-10 text-sm font-medium"
        >
          <Settings className="w-4 h-4 mr-1" />
          设置
        </Button>
        <Button
          onClick={handleEnterClick}
          className="flex-[2] bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-10 text-sm font-medium transition-colors"
        >
          进入
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default AgentCard;
